import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { ErrorComponent } from "@components/ErrorComponent";
import { Loader } from "@components/Loader";
import { MainLayout } from "@components/MainLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Body, BodySmall, Caption } from "@components/Typography";
import { Document, Routes } from "@ctypes";
import { useDocument, useDocuments, useErrorBanner } from "@hooks";
import { i18n, Translations } from "@i18n";
import { openInNewTab } from "@utils";
import { useStyles } from "./styles";
import {
  DocumentCategoryRouteState,
  DocumentItem,
  DocumentSection,
  GROUPED_TYPES,
  UNDATED_SECTION_KEY,
} from "./types";

const ISO_DATE_IN_TITLE = /\d{4}-\d{2}-\d{2}/;

const extractIsoDateFromTitle = (title: string): string => {
  const match = title.match(ISO_DATE_IN_TITLE);
  return match ? match[0] : "";
};

const formatSectionKey = (dateStr: string): string => {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) {
    return "";
  }

  const month = date.toLocaleString("en-US", { month: "short" }).toUpperCase();
  const year = date.getFullYear();
  return `${month} ${year}`;
};

const sectionKeySortValue = (key: string): number => {
  if (key === UNDATED_SECTION_KEY) {
    return Number.NEGATIVE_INFINITY;
  }
  const date = new Date(key.replace(/^([A-Z]{3}) (\d{4})$/, "$1 1, $2"));
  return isNaN(date.getTime()) ? 0 : date.getTime();
};

const groupDocumentsByMonth = (documents: DocumentItem[]): DocumentSection[] => {
  const sections = new Map<string, DocumentItem[]>();

  for (const doc of documents) {
    const key = formatSectionKey(doc.documentDate) || UNDATED_SECTION_KEY;

    const existing = sections.get(key) ?? [];
    existing.push(doc);
    sections.set(key, existing);
  }

  const entries = Array.from(sections.entries());
  entries.sort((a, b) => sectionKeySortValue(b[0]) - sectionKeySortValue(a[0]));

  return entries.map(([title, data]) => ({ title, data }));
};

export const DocumentCategoryRoute = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const styles = useStyles();
  const { showErrorBanner } = useErrorBanner();

  const state = (location.state as DocumentCategoryRouteState | undefined);
  const type = state?.type;
  const title = state?.title;

  useEffect(() => {
    if (!type || !title) {
      navigate(Routes.Documents, { replace: true });
    }
  }, [type, title, navigate]);

  const { documents, loading, error } = useDocuments(type);
  const { getDocument } = useDocument();

  if (!type || !title) {
    return null;
  }

  const normalizedDocs: DocumentItem[] =
    documents
      ?.filter((doc: Document) => doc.id && doc.title)
      .map((doc: Document) => {
        const docTitle = doc.title!;
        const dateFromApi = doc.documentDate;
        const dateFromTitle = extractIsoDateFromTitle(docTitle);
        return {
          id: doc.id!,
          title: docTitle,
          documentDate: dateFromApi || dateFromTitle || "",
          isRead: doc.isRead ?? true,
        };
      }) ?? [];

  const handleDocumentPress = async (documentId: string) => {
    try {
      const doc = await getDocument(documentId);
      const opened = openInNewTab(doc?.url);
      if (!opened) {
        showErrorBanner();
      }
    } catch {
      showErrorBanner();
    }
  };

  const isGrouped = GROUPED_TYPES.includes(type);

  if (loading) {
    return (
      <MainLayout showTab pageHeader={title}>
        <Loader />
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout showTab pageHeader={title}>
        <ErrorComponent />
      </MainLayout>
    );
  }

  const formatDisplayDate = (isoOrEmpty: string) => {
    if (!isoOrEmpty) {
      return "";
    }
    const date = new Date(isoOrEmpty);
    if (isNaN(date.getTime())) {
      return isoOrEmpty;
    }
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const renderDocumentCard = (item: DocumentItem) => (
    <div
      key={item.id}
      style={styles.card}
      onClick={() => handleDocumentPress(item.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleDocumentPress(item.id);
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div style={styles.cardContent}>
        <Body>{item.title}</Body>
        {item.documentDate ? (
          <>
            <Spacing size={SpacingSizes.XXS} />
            <Caption style={styles.dateText}>{formatDisplayDate(item.documentDate)}</Caption>
          </>
        ) : null}
      </div>
      {!item.isRead ? <span style={styles.unreadDot} /> : null}
    </div>
  );

  if (normalizedDocs.length === 0) {
    return (
      <MainLayout showTab pageHeader={title}>
        <div style={styles.emptyContainer}>
          <Body>{i18n.t(Translations.DocumentCategoryRouteEmptyTitle)}</Body>
          <Spacing size={SpacingSizes.XS} />
          <BodySmall>{i18n.t(Translations.DocumentCategoryRouteEmptySubtitle)}</BodySmall>
        </div>
      </MainLayout>
    );
  }

  if (isGrouped) {
    const sections = groupDocumentsByMonth(normalizedDocs);

    return (
      <MainLayout showTab pageHeader={title}>
        <div style={styles.listContent}>
          {sections.map((section) => (
            <div key={section.title}>
              <div style={styles.sectionHeader}>
                <span style={styles.sectionHeaderText}>
                  {section.title === UNDATED_SECTION_KEY
                    ? i18n.t(Translations.DocumentCategoryRouteSectionUndated)
                    : section.title}
                </span>
              </div>
              {section.data.map((item) => renderDocumentCard(item))}
            </div>
          ))}
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout showTab pageHeader={title}>
      <div style={styles.listContent}>{normalizedDocs.map(renderDocumentCard)}</div>
    </MainLayout>
  );
};
